<template>
    <AdminPageInner v-if="loaded" :breadcrumbs="breadcrumbs">
        <template #heading>Applications</template>
        <template #links>
            <router-link
                v-if="sessionGroup.user_can.apply_on_behalf"
                :to="{
                    name: 'admin.courses.session-groups.applications.create',
                    params: {courseId: sessionGroup.course_id, sessionGroupId: sessionGroup.id}
                }">
                Create
            </router-link>
        </template>
        <div class="sm:flex sm:items-center sm:space-x-4">
            <p>Filters:</p>
            <Select :options="statusFilters" v-model="status" class="w-full max-w-sm"/>
        </div>

        <PaginationWrapper
            ref="pagination"
            :rows="rows"
            :constraints="constraints"
            :meta="meta"
            class="mt-6">
            <DataComponent
                class="w-full mt-6"
                :data="rows"
                :initialSort="initialSort"
                @sort="onSort">
                <DataField name="user_name" label="Name"/>
                <DataField name="status" label="Status" class="w-1/2">
                    <template v-slot="{row}">
                        <div class="relative flex items-center">
                            <div class="absolute text-xs" :class="{'hidden': isEditing(row.id)}">
                                <ApplicationStatus :status="row.status"/>
                            </div>
                            <div :class="{'opacity-0 pointer-events-none': !isEditing(row.id)}">
                                <Button
                                    :class="{'opacity-50 cursor-not-allowed': !hasAvailability && row.status !== CourseApplicationStatus.Accepted}"
                                    :theme="row.status === CourseApplicationStatus.Accepted ? 'primary' : 'plain'"
                                    @click.prevent="update(row.id, CourseApplicationStatus.Accepted)"
                                    :disabled="!hasAvailability && row.status !== CourseApplicationStatus.Accepted">
                                    Accept
                                </Button>
                                <Button
                                    class="ml-4"
                                    :theme="row.status === CourseApplicationStatus.Rejected ? 'primary' : 'plain'"
                                    @click.prevent="update(row.id, CourseApplicationStatus.Rejected)">
                                    Reject
                                </Button>
                            </div>
                        </div>
                    </template>
                </DataField>
                <DataField
                    name="actions"
                    class="md:w-40"
                    :sortable="false"
                    :showLabel="false"
                    :showResponsiveLabel="false">
                    <template v-slot="{row}">
                        <div class="flex justify-around md:justify-end items-center">
                            <template v-if="row.status !== CourseApplicationStatus.Cancelled">
                                <button
                                    v-if="row.user_can.update && !isEditing(row.id)"
                                    class="text-indigo-600 hover:text-indigo-900 mr-4"
                                    @click.prevent="edit(row.id)">
                                    Edit
                                </button>
                                <button
                                    v-if="isEditing(row.id)"
                                    class="text-gray-600 hover:text-gray-900 mr-4"
                                    @click.prevent="cancelEdit(row.id)">
                                    Cancel
                                </button>
                            </template>
                        </div>
                    </template>
                </DataField>
            </DataComponent>
        </PaginationWrapper>
    </AdminPageInner>
</template>

<script>

import axios from "axios";
import {CourseApplicationStatus} from "@/Lib/Enums";
import AdminPageInner from "@/Components/Layout/AdminPageInner.vue";
import PaginationWrapper from "@/Components/Layout/PaginationWrapper.vue";
import PaginationPage from "@/Mixins/PaginationPage";
import Select from "@/Components/Form/Select.vue";
import {DataComponent, DataField} from "@/Components/DataComponent";
import Button from "@/Components/Button";
import ApplicationStatus from "@/Components/ApplicationStatus";

export default {

    name: "CourseApplicationsIndexPage",

    mixins: [PaginationPage],

    data() {
        return {
            CourseApplicationStatus,
            sessionGroup: {},
            statusFilters: [
                {label: "All", value: null},
                {label: "Pending", value: CourseApplicationStatus.Pending},
                {label: "Accepted", value: CourseApplicationStatus.Accepted},
                {label: "Rejected", value: CourseApplicationStatus.Rejected},
            ],
            editing: [],
            processing: []
        };
    },

    components: {
        AdminPageInner,
        PaginationWrapper,
        DataComponent,
        DataField,
        ApplicationStatus,
        Select,
        Button,
    },

    methods: {
        setData({applications, sessionGroup}) {
            PaginationPage.methods.setData.call(this, applications);

            // Clear the application ids from the editing array that aren't present in the current request
            const applicationIds = applications.data.map(item => item.id);

            this.editing = this.editing.filter(id => applicationIds.indexOf(id) !== -1)

            if (sessionGroup) {
                this.sessionGroup = sessionGroup;
                this.course = sessionGroup.course;
            }
        },
        edit(id) {
            if (!this.isEditing(id)) {
                this.editing.push(id);
            }
        },
        isEditing(id) {
            return this.editing.indexOf(id) !== -1;
        },
        cancelEdit(id) {
            this.editing = this.editing.filter(item => item !== id);
        },
        async update(id, status) {
            const application = this.rows.find(application => application.id === id);

            if (application.status === status) {
                this.cancelEdit(id);
                return;
            }

            try {
                await axios.patch(route('api.course-applications.update', id), {status});

                this.$notify.success('Application updated');
                if (status === CourseApplicationStatus.Accepted) {
                    this.sessionGroup.accepted_applications_count++;
                } else {
                    this.sessionGroup.rejected_applications_count++;
                }

                switch (application.status) {
                    case CourseApplicationStatus.Pending:
                        this.sessionGroup.pending_applications_count--;
                        break;
                    case CourseApplicationStatus.Accepted:
                        this.sessionGroup.accepted_applications_count--;
                        break;
                    case CourseApplicationStatus.Rejected:
                        this.sessionGroup.rejected_applications_count--;
                        break;
                }

                application.status = status;
                this.cancelEdit(id);

            } catch (e) {
                console.log(e);
            } finally {

            }
        },
    },

    computed: {
        breadcrumbs() {
            return [
                {
                    label: 'Admin', to: {name: 'admin'}
                },
                {
                    label: 'Courses', to: {name: 'admin.courses.index'}
                },
                {
                    label: `${this.course.title || 'Course'}`,
                    to: {name: 'admin.courses.show', params: {courseId: this.$route.params.courseId}}
                },
                {
                    label: `Session Groups`,
                    to: {name: 'admin.courses.session-groups.index', params: {courseId: this.$route.params.courseId}}
                },
                {
                    label: `Group`,
                    to: {
                        name: 'admin.courses.session-groups.show',
                        params: {
                            courseId: this.$route.params.courseId,
                            courseSessionGroupId: this.$route.params.courseSessionGroupId
                        }
                    }
                },
                {
                    label: `Applications`, to: {name: this.$route.name, params: this.$route.params}
                },
            ];
        },
        status: {
            get() {
                return this.constraints.status ? parseInt(this.constraints.status) : null;
            },
            set(status) {
                this.$refs.pagination.getData(_.merge(this.constraints, {status, page: null}));
            },
        },
        hasAvailability() {
            return this.sessionGroup.spaces > this.sessionGroup.accepted_applications_count;
        },
    },

    async beforeRouteEnter(to, from, next) {

        const data = await fetchAllData(to);
        next(vm => vm.setData(data));
    },

    async beforeRouteUpdate(to, from) {

        let data = {};

        if (to.params.courseSessionGroupId !== from.params.courseSessionGroupId) {
            data = fetchAllData(to);
        } else {
            data.applications = (await fetchApplications(to)).data;
        }

        this.setData(data);
    },
};

const fetchAllData = async to => {

    const data = await Promise.all([
        fetchApplications(to),
        axios.get(route('api.course-session-groups.show', to.params.courseSessionGroupId)),
    ])

    return {
        applications: data[0].data,
        sessionGroup: data[1].data
    };
}

const fetchApplications = async (to) => {
    return axios.get(route('api.course-applications.index'), {
        params: Object.assign(to.query, {course_session_group_id: to.params.courseSessionGroupId, per_page: 50})
    });
}
</script>
